/**
 * 画布右侧竖条：选择模式 / 小手平移模式切换（样式对齐主题切换条）
 */
import { useEffect, useState } from 'react';
import { IconHandStop, IconPointer } from '@tabler/icons-react';
import { Button, Popover, cn } from '@heroui/react';
import { PropertyPanelBody, usePropertyPanelHeaderTitle } from '@/components/canvas/designer/inspector';

export interface CanvasPointerModeSwitcherProps {
  /** 小手模式是否处于激活态（含空格临时切换） */
  isHandActive: boolean;
  onSelectPointer: () => void;
  onSelectHand: () => void;
  className?: string;
  /** 属性面板打开时通知外部（如关闭图片侧栏） */
  onPropertyPanelOpenChange?: (open: boolean) => void;
  /** 注册关闭属性面板，供顶栏工具条等调用 */
  onRegisterClosePropertyPanel?: (close: () => void) => void;
}

const PROPERTY_LABEL_MAX_CHARS = 3;
/** 三字竖排内容区高度（text-sm × 3 行） */
const PROPERTY_LABEL_BLOCK_HEIGHT = '4.25rem';

/** 竖排最多三字；固定三行高，不足三字时仍上下居中 */
function VerticalPropertyLabel({ label }: { label: string }) {
  const chars = [...label].slice(0, PROPERTY_LABEL_MAX_CHARS);
  return (
    <span
      className="flex w-full flex-col items-center justify-center overflow-hidden text-sm leading-none"
      style={{ height: PROPERTY_LABEL_BLOCK_HEIGHT }}
      aria-hidden
    >
      {chars.map((ch, i) => (
        <span key={i} className="block w-full shrink-0 text-center leading-none">
          {ch}
        </span>
      ))}
    </span>
  );
}

export function CanvasPointerModeSwitcher({
  isHandActive,
  onSelectPointer,
  onSelectHand,
  className,
  onPropertyPanelOpenChange,
  onRegisterClosePropertyPanel,
}: CanvasPointerModeSwitcherProps) {
  const [propsPopoverOpen, setPropsPopoverOpen] = useState(false);
  const propertyButtonLabel = usePropertyPanelHeaderTitle();

  useEffect(() => {
    onRegisterClosePropertyPanel?.(() => setPropsPopoverOpen(false));
  }, [onRegisterClosePropertyPanel]);

  const handlePropertyPanelOpenChange = (open: boolean) => {
    setPropsPopoverOpen(open);
    onPropertyPanelOpenChange?.(open);
  };

  return (
    <div
      className={cn(
        'pointer-events-none absolute inset-y-0 right-3 z-[42] flex w-11 items-center justify-center',
        className,
      )}
      role="presentation"
    >
      <div className="pointer-events-auto flex w-9 flex-col items-stretch space-y-1.5">
        <div
          className="flex flex-col items-center gap-0.5 rounded-full bg-default p-0.5 backdrop-blur-sm"
          role="group"
          aria-label="鼠标左键模式"
        >
          <Button
            type="button"
            variant={!isHandActive ? 'primary' : 'ghost'}
            size="sm"
            isIconOnly
            className="size-8 rounded-full"
            aria-label="选择模式"
            aria-pressed={!isHandActive}
            onPress={onSelectPointer}
          >
            <IconPointer className="size-4" stroke={1.25} />
          </Button>
          <Button
            type="button"
            variant={isHandActive ? 'primary' : 'ghost'}
            size="sm"
            isIconOnly
            className="size-8 rounded-full"
            aria-label="拖拽平移画布"
            aria-pressed={isHandActive}
            onPress={onSelectHand}
          >
            <IconHandStop className="size-4" stroke={1.25} />
          </Button>
        </div>

        <Popover isOpen={propsPopoverOpen} onOpenChange={handlePropertyPanelOpenChange}>
          {/* 竖向胶囊：宽同指针组，高固定为三字；仅外层 rounded-full，内钮勿再圆形 */}
          <Popover.Trigger
            className="box-border flex w-full cursor-default overflow-hidden rounded-full bg-default p-0.5 shadow-none backdrop-blur-sm outline-none focus-visible:!ring-0 focus-visible:outline-none data-[focus-visible=true]:!ring-0 data-[focus-visible=true]:outline-none"
            style={{
              height: `calc(0.25rem + ${PROPERTY_LABEL_BLOCK_HEIGHT})`,
            }}
          >
            <Button
              type="button"
              variant={propsPopoverOpen ? 'primary' : 'ghost'}
              size="sm"
              className="box-border flex size-full min-h-0 min-w-0 items-center justify-center overflow-hidden rounded-full p-0 shadow-none outline-none focus-visible:!ring-0 focus-visible:outline-none data-[focus-visible=true]:!ring-0 data-[focus-visible=true]:outline-none"
              aria-label={propertyButtonLabel}
              aria-expanded={propsPopoverOpen}
            >
              <VerticalPropertyLabel label={propertyButtonLabel} />
            </Button>
          </Popover.Trigger>
          <Popover.Content
            placement="left"
            offset={6}
            className="flex max-h-[min(80vh,640px)] flex-col overflow-hidden p-0"
            style={{ width: `min(92vw, 300px)` }}
          >
            <Popover.Dialog className="flex min-h-0 max-h-[inherit] flex-1 flex-col overflow-hidden p-0 outline-none">
              <PropertyPanelBody omitHeader className="min-h-0 flex-1" />
            </Popover.Dialog>
          </Popover.Content>
        </Popover>
      </div>
    </div>
  );
}
